import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialUserState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const { userEndPoint, passwordChangeEndPoint, userProfileEndPoint } = endPoints;

export const fetchAllUsers = createAsyncThunk(
  "users/fetchAllUsers",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: userEndPoint,
      searchParams: { ...searchParamsObj },
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserDetail = createAsyncThunk(
  "users/fetchUserDetail",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      //   console.log({ userId });

      const response: AxiosResponse = await crudService.getById(
        userEndPoint,
        userId
      );
      //   console.log({ response });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async ({ userId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(userEndPoint, userId);

      if (response.status === 204) {
        NotificationManager.success("User Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewUser = createAsyncThunk(
  "users/addNewUser",
  async (newUser: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${userEndPoint}/`,
        newUser
      );

      if (response.status === 201) {
        NotificationManager.success("User Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ updatedUser, userId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        userEndPoint,
        userId,
        updatedUser
      );
      if (response.status === 204) {
        NotificationManager.success("User data updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "users/updatePassword",
  async ({ updatedUser }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${passwordChangeEndPoint}/`,
        updatedUser
      );
      if (response.status === 200) {
        NotificationManager.success(
          response?.data?.message || "Password updated Successfully."
        );
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

export const getUserProfile = createAsyncThunk(
  "users/getUserProfile",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.getAll(
        `${userProfileEndPoint}?user__id=${userId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState: initialUserState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all users
    builder
      .addCase(fetchAllUsers.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllUsers.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.users = action.payload.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllUsers.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // delete users
    builder
      .addCase(deleteUser.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteUser.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new user
    builder
      .addCase(addNewUser.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addNewUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addNewUser.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update users
    builder
      .addCase(updateUser.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateUser.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateUser.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   update user password
    builder
      .addCase(updatePassword.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updatePassword.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updatePassword.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   user detail
    builder
      .addCase(fetchUserDetail.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchUserDetail.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.userDetail = action?.payload;
        }
      )
      .addCase(
        fetchUserDetail.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.userDetail = null;
        }
      );

    //   user profile for login user
    builder
      .addCase(getUserProfile.pending, (state: any) => {
        state.profileLoading = true;
      })
      .addCase(
        getUserProfile.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.profileLoading = false;
          state.userProfile = action?.payload?.objects[0] || {};
        }
      )
      .addCase(
        getUserProfile.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.profileLoading = false;
          state.error = action.payload as string;
          state.userProfile = null;
        }
      );
  },
});

export const usersReducer = usersSlice.reducer;
